import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {GitPullRequestIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {Link, SkeletonBox, Tooltip, VisuallyHidden} from '@primer/react'
import {graphql, useFragment, usePreloadedQuery, type PreloadedQuery} from 'react-relay'
import styles from './RepositoryLabel.module.css'
import {Suspense} from 'react'
import type {IssuesAndPullRequestsCount$key} from './__generated__/IssuesAndPullRequestsCount.graphql'
import type {IssuesAndPullRequestsCountSecondaryQuery} from './__generated__/IssuesAndPullRequestsCountSecondaryQuery.graphql'

type IssuesAndPullRequestsCount = {
  secondaryQueryRef?: PreloadedQuery<IssuesAndPullRequestsCountSecondaryQuery> | null
  labelName: string
  labelId: string
}

export const IssuesAndPullRequestsCountSecondaryQueryQ = graphql`
  query IssuesAndPullRequestsCountSecondaryQuery($nodes: [ID!]!) {
    nodes(ids: $nodes) {
      id
      ... on Label {
        ...IssuesAndPullRequestsCount
      }
    }
  }
`

export const IssuesAndPullRequestsCount = ({secondaryQueryRef, labelName, labelId}: IssuesAndPullRequestsCount) => {
  if (!secondaryQueryRef) return null

  return (
    <Suspense fallback={<LoadingLabelSecondaryData />}>
      <IssuesAndPullRequestsCountFetched
        labelName={labelName}
        labelId={labelId}
        secondaryQueryRef={secondaryQueryRef}
      />
    </Suspense>
  )
}

const LoadingLabelSecondaryData = () => {
  return (
    <ListItemDescriptionItem className={styles.labelRowIssuesAndPrsCount}>
      <SkeletonBox className={styles.loadingIssueAndPullRequestCount} />
      <SkeletonBox className={styles.loadingIssueAndPullRequestCount} />
    </ListItemDescriptionItem>
  )
}

export const IssuesAndPullRequestsCountFetched = ({
  secondaryQueryRef,
  labelName,
  labelId,
}: IssuesAndPullRequestsCount & {secondaryQueryRef: PreloadedQuery<IssuesAndPullRequestsCountSecondaryQuery>}) => {
  const {nodes} = usePreloadedQuery<IssuesAndPullRequestsCountSecondaryQuery>(
    IssuesAndPullRequestsCountSecondaryQueryQ,
    secondaryQueryRef,
  )

  const labelNode = nodes?.find(node => node?.id === labelId)

  if (!labelNode) return null

  return <IssuesAndPullRequestsCountInternal labelName={labelName} labelNode={labelNode} />
}

export const IssuesAndPullRequestsCountInternal = ({
  labelName,
  labelNode,
}: {
  labelName: string
  labelNode: IssuesAndPullRequestsCount$key
}) => {
  const data = useFragment(
    graphql`
      fragment IssuesAndPullRequestsCount on Label {
        issues(states: OPEN) {
          totalCount
        }
        pullRequests(states: OPEN) {
          totalCount
        }
      }
    `,
    labelNode,
  )

  const openIssues = data.issues?.totalCount || 0
  const openIssuesQuery = `issues?q=${encodeURIComponent(`is:open label:"${labelName}"`)}`
  const openPullRequests = data.pullRequests?.totalCount || 0
  const openPullRequestsQuery = `issues?q=${encodeURIComponent(`is:open is:pr label:"${labelName}"`)}`

  return (
    <ListItemDescriptionItem className={styles.labelRowIssuesAndPrsCount}>
      {openIssues > 0 ? (
        <Tooltip text={`${openIssues} open issues`} direction="n">
          <Link href={openIssuesQuery} muted>
            <IssueOpenedIcon size={16} /> {openIssues} <VisuallyHidden>open issues</VisuallyHidden>
          </Link>
        </Tooltip>
      ) : null}
      {openPullRequests > 0 ? (
        <Tooltip text={`${openPullRequests} open pull requests`} direction="n">
          <Link href={openPullRequestsQuery} muted>
            <GitPullRequestIcon size={16} /> {openPullRequests} <VisuallyHidden>open pull requests</VisuallyHidden>
          </Link>
        </Tooltip>
      ) : null}
    </ListItemDescriptionItem>
  )
}

try{ IssuesAndPullRequestsCount.displayName ||= 'IssuesAndPullRequestsCount' } catch {}
try{ LoadingLabelSecondaryData.displayName ||= 'LoadingLabelSecondaryData' } catch {}
try{ IssuesAndPullRequestsCountFetched.displayName ||= 'IssuesAndPullRequestsCountFetched' } catch {}
try{ IssuesAndPullRequestsCountInternal.displayName ||= 'IssuesAndPullRequestsCountInternal' } catch {}